import { useContext } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { AppContext } from '@/app.context';

import { getIssueStorageBreakdown, type IssueStorageBreakdown } from './issue-storage';

/**
 * How much of a downloaded issue's storage is exclusively its own, for the
 * downloads panel's per-issue insight. Reads only local IndexedDB (Dexie
 * rows + the `cf-issue-index` Cache Storage index), so it works fully
 * offline like everything else `entities/downloaded-issue` does.
 *
 * Deliberately **not** run through the app's default query persister
 * (`persister: undefined` overrides `defaultQueryClient`'s default) and uses
 * `staleTime: 0` -- unlike server data, there's no network round trip to
 * save here, the underlying reads are cheap, and persisting a byte count
 * across app restarts risks showing a stale number from before the last
 * session's downloads/removals for a value that costs nothing to just
 * recompute on mount instead.
 *
 * Scoped to one already-downloaded issue -- `issueId` is expected to be a
 * fully-downloaded issue's id (the downloads panel never lists anything
 * else). A future whole-PWA storage view can build its own aggregate on top
 * of `getIssueStorageBreakdown` without this hook needing to change.
 */
export function useIssueStorage(
	issueId: string | undefined,
): UseQueryResult<IssueStorageBreakdown> {
	const { db } = useContext(AppContext);

	return useQuery({
		queryKey: ['issue-storage', issueId] as const,
		queryFn: () => {
			if (!db || !issueId) {
				throw new Error('useIssueStorage: missing db or issueId');
			}
			return getIssueStorageBreakdown(db, issueId);
		},
		enabled: !!db && !!issueId,
		staleTime: 0,
		persister: undefined,
	});
}
